<template>
  <div class="open-ended-grouping" :class="{'white-text': isTextColourWhite}">
    <div
      class="open-ended-grouping--pill animate__animated animate__fadeIn animate__faster"
      @click="handleGroupOpenEnded()"
      :class="openEndedGroupingClasses"
      v-tooltip="tooltipLabel"
    >
      <div
        class="toggler__wrapper"
        :class="{ 'showing-grouping-word-cloud': isOpenEndedGroupingLayout }"
      >
        <div class="grouping-button__content">
          <span
            class="loading-spinner"
            v-if="isOpenEndedGroupingLoading"
            :class="{'black': slideActive.textColour == '#313131'}"
          />
          <aha-icon
            name="grouping-open-ended"
            v-if="!isOpenEndedGroupingLoading"
          ></aha-icon>
          <p class="ml-1.5 font-bold">
            {{ buttonLabel }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import OpenAIApi from '@/api/openai'
import EventBus from '@/eventBus'
import Utils from '@/util'
import { PRESENTING_MODE } from '@/constant/eventCategories'

const RESPONSES_NEEDED_FOR_GROUPING = 10

export default {
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      isOpenEndedGroupingLayout: (state) => state.slideActive.isOpenEndedGroupingLayout,
      isOpenEndedGroupingLoading: (state) => state.slideActive.isOpenEndedGroupingLoading,
      openEndedAIGroupedAnswers: (state) => state.slideActive.openEndedAIGroupedAnswers,
      openEndedResponseCount: (state) => state.slideActive.openededCount || 0,
      userOnline: (state) => state.userOnline,
    }),

    openEndedGroupingClasses() {
      return {
        'is-hight-lighted': this.isGroupButtonHighLighted,
        'is-disabled': this.isGroupButtonDisabled,
      }
    },
    tooltipLabel() {
      return this.isGroupButtonDisabled ? this.$i18nPresenter.t('Group responses to get key ideas when reaching 10 responses') : null
    },
    buttonLabel() {
      return this.isOpenEndedGroupingLayout ? this.$t('Ungroup') : this.$t('Group')
    },
    isTextColourWhite() {
      return this.slideActive.textColour === '#ffffff'
    },
    isGroupButtonDisabled() {
      return this.openEndedResponseCount < RESPONSES_NEEDED_FOR_GROUPING
    },
    isGroupButtonHighLighted() {
      return !this.isGroupButtonDisabled && !this.isOpenEndedGroupingLayout
    },
    hasGroupedAnswers() {
      return this.openEndedAIGroupedAnswers && this.openEndedAIGroupedAnswers.length > 0
    },
  },

  mounted() {
    EventBus.$on('open-ended-smart-grouping: generated', (socketData) => {
      if (socketData.error) {
        Utils.showToastError(this.$i18nPresenter.t('Failed to group responses.'))
        return
      }
      this.updateSlideActiveAction({
        openEndedAIGroupedAnswers: socketData.result,
        isOpenEndedGroupingLayout: true,
        isOpenEndedGroupingLoading: false,
      })
      EventBus.$emit('ai-open-ended-grouping: redraw_masonry')
    })
  },

  destroyed() {
    EventBus.$off('open-ended-smart-grouping: generated')
  },

  methods: {
    ...mapActions([
      'updateSlideActiveAction',
    ]),

    async _groupOpenEnded() {
      try {
        this.updateSlideActiveAction({
          isOpenEndedGroupingLoading: true,
        })
        await OpenAIApi.groupOpenEndedAnswers(this.slideActive.id, {
          question: this.slideActive.title,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
        })
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Failed to group responses.'))
      }
    },

    _trackClickGroupOpenEnded() {
      const eventAction = this.isOpenEndedGroupingLayout ? 'Grouping > Ungroup Words' : 'Grouping > Group Words'
      const trackData = {
        eventAction,
        eventCategory: PRESENTING_MODE,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
        submissionCount: this.openEndedResponseCount || 0,
        audienceCount: this.userOnline || 0,
        slideId: this.slideActive.id,
        slideType: 'openEnded',
      }
      Utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    async handleGroupOpenEnded() {
      try {
        if (this.isOpenEndedGroupingLoading || this.isGroupButtonDisabled) return
        this._trackClickGroupOpenEnded()
        if (this.hasGroupedAnswers) {
          this.updateSlideActiveAction({
            isOpenEndedGroupingLayout: !this.isOpenEndedGroupingLayout,
          })
          return
        }
        this._groupOpenEnded()
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Failed to group responses.'))
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.open-ended-grouping {
  @apply ml-4 relative;
  &:before {
    width: 1px;
    content: "";
    left: 0;
    top: 0.4rem;
    height: 25px;
    position: absolute;
    background-color: #D3D7E1;
    transition: background-color 0.2s ease-out;
  }
  &.white-text {
    &:before {
      background-color: rgba(255, 255, 255, 0.2);
    }
    .open-ended-grouping--pill {
      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
      &.is-hight-lighted:hover {
        @apply bg-primary-100;
      }
      &.is-disabled {
        color: rgba(255,255,255,0.5);
        @apply cursor-not-allowed;
        &:hover {
          background-color: unset;
        }
      }
    }
  }
}
.open-ended-grouping--pill {
  @apply rounded-3xl cursor-pointer ml-2;
  background-color: rgba(255, 255, 255, 0);
  padding: 0.4rem 0.75rem;
  &:hover {
    @apply bg-base-30;
  }
  &.is-disabled {
    color: rgba(0,0,0,0.5);
    @apply cursor-not-allowed;
    &:hover {
      background-color: unset;
    }
  }
  &.is-hight-lighted {
    @apply bg-primary-90 text-base-0;
    &:hover {
      @apply bg-primary-100;
    }
    .loading-spinner {
      border: 0.15em solid rgba(255, 255, 255, 0.3);
      border-top-color: #ffffff;
    }
  }
}
.toggler__wrapper {
  @apply flex text-base;
  .grouping-button__content {
    @apply inline-flex;
  }
}
.loading-spinner {
  width: 1.25em;
  height: 1.25em;
  margin: 2px;
}
</style>
